import { useState } from 'react';
import { toast } from 'react-toastify';
import { fetchLatestInventoryUnitsCSV } from 'src/services/client';
import { CsvDateFormat } from 'src/services/client-types';
import { downloadCSV } from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/helpers';

export interface UseDownloadUnitsCSVReturn {
  isLoading: boolean;
  error: string | null;
  onDownloadCSV: (
    propertyId: number,
    dateFormat: CsvDateFormat
  ) => Promise<boolean>;
  clearError: () => void;
}

export const useDownloadUnitsCSV = (): UseDownloadUnitsCSVReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const onDownloadCSV = async (
    propertyId: number,
    dateFormat: CsvDateFormat
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    let csvString: string;

    try {
      csvString = await fetchLatestInventoryUnitsCSV(propertyId, dateFormat);
    } catch (err: unknown) {
      const errorMessage: string =
        err instanceof Error
          ? err.message
          : 'Units CSV download failed, please try again.';

      console.error('Units CSV download failed:', err);
      setError(errorMessage);
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      setIsLoading(false);
      return false;
    }

    const timestamp: string = new Date().toISOString().split('T')[0];
    const filename: string = `suite-export-${propertyId}-${timestamp}.csv`;

    downloadCSV(csvString, filename);

    toast.success('Units CSV downloaded.', {
      autoClose: 5000,
      position: 'top-center',
    });
    setIsLoading(false);
    return true;
  };

  const clearError = (): void => {
    setError(null);
  };

  return {
    isLoading,
    error,
    onDownloadCSV,
    clearError,
  };
};
